/**
 * One of the four stat tiles across the top of the Analytics tab (task 1304),
 * as the design file's stage 1 draws them: the period's value, its change
 * against the preceding period of the same length, a one-line sub-figure and a
 * sparkline of the period's daily values.
 *
 * Status colors appear only on the delta, always with a sign: green when the
 * change is good for the metric, red when it is bad, so color is never the
 * only cue. The sparkline is a recessive grey line with the first series slot
 * on its last point, like the design file's `spark()`.
 */
import { ANALYTICS_SERIES_SLOTS } from './DailyChart';
import type { StatDelta } from './analyticsFormat';

const SPARK_W = 92;
const SPARK_H = 28;

interface StatTileProps {
  label: string;
  value: string;
  /**
   * The change against the comparison period: undefined while that period is
   * still loading (nothing shown), null when it cannot be compared ("—").
   */
  delta: StatDelta | null | undefined;
  /** "vs previous 30 days". */
  deltaLabel: string;
  /** The tile's one-line sub-figure, such as "81 a day". */
  sub: string;
  /** Extra lines under the sub-figure. */
  note?: string;
  spark: (number | null)[];
  testId: string;
}

/** A plain-SVG sparkline; a day with no value is a gap. */
function Sparkline({ values }: { values: (number | null)[] }) {
  const known = values.filter((v): v is number => v !== null);
  if (known.length === 0) return <svg width={SPARK_W} height={SPARK_H} aria-hidden="true" className="hidden sm:block" />;
  const max = Math.max(...known);
  const min = Math.min(...known);
  const x = (i: number) => 2 + (values.length > 1 ? (i * (SPARK_W - 6)) / (values.length - 1) : (SPARK_W - 6) / 2);
  const y = (v: number) => 2 + (SPARK_H - 6) * (1 - (v - min) / (max - min || 1));
  let d = '';
  let pen = false;
  values.forEach((v, i) => {
    if (v === null) {
      pen = false;
      return;
    }
    d += `${pen ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`;
    pen = true;
  });
  let last = values.length - 1;
  while (last >= 0 && values[last] === null) last -= 1;
  // Too wide beside the value on a phone-width tile, so shown from `sm` up.
  return (
    <svg width={SPARK_W} height={SPARK_H} aria-hidden="true" className="hidden justify-self-end sm:block">
      <path d={d} fill="none" strokeWidth={1.5} strokeLinejoin="round" className="stroke-gray-400 dark:stroke-gray-500" />
      <circle cx={x(last)} cy={y(values[last] as number)} r={3.5} className="fill-white dark:fill-gray-800" />
      <circle cx={x(last)} cy={y(values[last] as number)} r={2.5} className={ANALYTICS_SERIES_SLOTS[0].fill} />
    </svg>
  );
}

const TONE_CLASS = {
  good: 'text-green-600 dark:text-green-400',
  bad: 'text-red-600 dark:text-red-400',
  neutral: 'text-gray-700 dark:text-gray-300',
} as const;

export function StatTile({ label, value, delta, deltaLabel, sub, note, spark, testId }: StatTileProps) {
  return (
    <div
      data-testid={testId}
      className="grid grid-cols-[1fr_auto] content-start items-end gap-x-2.5 gap-y-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2.5 dark:border-gray-700 dark:bg-gray-800"
    >
      <span className="col-span-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</span>
      <span data-testid={`${testId}-value`} className="whitespace-nowrap text-2xl font-semibold leading-tight text-gray-900 tabular-nums dark:text-gray-100">
        {value}
      </span>
      <Sparkline values={spark} />
      <span className="col-span-2 text-xs text-gray-500 dark:text-gray-400">
        {delta !== undefined && (
          <>
            <b data-testid={`${testId}-delta`} className={`font-semibold ${delta ? TONE_CLASS[delta.tone] : TONE_CLASS.neutral}`}>
              {delta ? delta.text : '—'}
            </b>{' '}
            {deltaLabel} ·{' '}
          </>
        )}
        {sub}
        {note && <span className="block">{note}</span>}
      </span>
    </div>
  );
}

export default StatTile;
